/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from "react";
import { ButtonStyle } from "@/types/project";
import { cn } from "@/lib/utils";
import { PageData } from "@/types/builder";

interface ProjectStyleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonStyle: ButtonStyle;
  isHovered?: boolean;
  buttonType?: "externalUrl" | "page" | "email" | "phone" | "popup" | "";
  buttonUrl?: string;
  pageId?: string;
  pagelink?: string;
  emailField?: string;
  phoneField?: string;
  popupId?: string;
  buttonTarget?: "_blank";
  onPopupOpen?: (popupId: string) => void; // callback to open popup
  pages?: PageData[];
  children: React.ReactNode;
}

export const ProjectStyleButton = forwardRef<
  HTMLButtonElement,
  ProjectStyleButtonProps
>(
  (
    {
      buttonStyle,
      isHovered,
      buttonType,
      buttonUrl,
      pageId,
      pagelink,
      emailField,
      phoneField,
      popupId,
      buttonTarget = "_blank",
      onPopupOpen,
      pages,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    // Default button style if none provided
    const safeButtonStyle: ButtonStyle = buttonStyle || {
      style: "rounded",
      defaultColor: "#3b82f6",
      textColor: "#ffffff",
      borderColor: "#3b82f6",
      fillColor: "#3b82f6",
      isTransparent: false,
      exploreButtonradius: 10,
      font: "inter",
      fontweight: "",
      fontSize: "16px",
      Italic: false,
      Underline: "none",
      buttonBottomPadding: "0px",
    };

    const getBorderRadius = () => {
      // switch (safeButtonStyle.style) {
      //   case "rounded":
      //     return "0.5rem";
      //   case "square":
      //     return "0";
      //   case "pill":
      //     return "9999px";
      //   default:
      //     return "0.375rem";
      // }
      return safeButtonStyle.exploreButtonradius;
    };

    const fillColor = safeButtonStyle.isTransparent
      ? "transparent"
      : safeButtonStyle.fillColor || safeButtonStyle.defaultColor;

    const borderColor = safeButtonStyle.borderColor || "#ffffff00";
    const hoverBorderColor =
      safeButtonStyle.fillColor || safeButtonStyle.defaultColor;

    const textColor = isHovered
      ? safeButtonStyle.textColor || "#ffffff"
      : safeButtonStyle.isTransparent
        ? borderColor
        : safeButtonStyle.textColor || "#ffffff";

    // Determine click behavior based on buttonType
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Call parent onClick if provided
      if (props.onClick) {
        props.onClick(e);
      }

      switch (buttonType) {
        case "externalUrl":
          if (buttonUrl) {
            const link = document.createElement("a");
            link.href = buttonUrl;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.click();
          }
          break;
        case "page":
          if (pageId && pages) {
            const targetPage = pages.find((p: any) => p._id === pageId);
            if (targetPage) {
              // For viewer context: use setSelectedPage pattern via onClick
              // For direct navigation: construct slug-based URL
              if (pagelink) {
                console.log(pagelink);
                window.location.href = pagelink;
              } else if (targetPage.slug) {
                // Stay within the viewer - scroll to top and let parent handle
                // The parent onClick (handleButtonClick) should handle page selection
              }
            }
          } else if (pagelink) {
            window.location.href = pagelink;
          }
          break;
        case "email":
          if (emailField) window.location.href = `mailto:${emailField}`;
          break;
        case "phone":
          if (phoneField) window.location.href = `tel:${phoneField}`;
          break;
        case "popup":
          if (popupId && onPopupOpen) onPopupOpen(popupId);
          break;
        default:
          break;
      }
    };

    const withPx = (value) => {
      if (value == null) return undefined;
      if (typeof value === "number") return `${value}px`;

      const str = value.toString().trim();

      // already has unit
      if (/[a-z%]+$/i.test(str)) return str;

      return `${str}px`;
    };

    return (
      <button
        ref={ref}
        className={cn(
          "px-6 py-2 font-semibold transition-all duration-300",
          className,
        )}
        style={{
          backgroundColor: isHovered
            ? safeButtonStyle?.fillColor || fillColor || "#000"
            : fillColor || "#000",

          borderColor: borderColor,

          opacity: isHovered ? 0.9 : 1,
          transition: "all 0.3s ease",

          borderWidth: "2px",
          borderStyle: "solid",
          color: textColor || "#fff",
          borderRadius: getBorderRadius() || "6px",
          fontFamily: safeButtonStyle.font,
          fontSize: withPx(safeButtonStyle.fontSize),

          fontWeight: safeButtonStyle.fontweight,

          fontStyle: safeButtonStyle.Italic ? "italic" : undefined,
          textDecoration:
            safeButtonStyle.Underline && safeButtonStyle.Underline !== "none"
              ? safeButtonStyle.Underline
              : undefined,
        }}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  },
);

ProjectStyleButton.displayName = "ProjectStyleButton";
